import { useFormikContext } from "formik";
import { useEffect, useMemo, useRef } from "react";
import { useListedAlgo } from "../lazertrader/contexts/listAlgosContext";
import { Algo, AlgoConfiguration, direction } from "../lazertrader/types";
import { focusField } from "../lazertrader/utils";
import {
  ConfigTabState,
  customResetForm,
  initialState,
  takesSpotReference,
} from "./types";
import { v4 as uuidv4 } from "uuid";
import dayjs, { Dayjs } from "dayjs";
import { find } from "../orderBook/allGrids";
import { usePrefillContext } from "../../Services/PrefillContextProvider";
import {
  Behavior,
  behaviorFromAlgoSpec,
  DEFAULT_TERMINATOR_BEHAVIOR,
  isShimuraiAlgoSpec,
  isTerminatorBehavior,
} from "./algoSpec";
import { Price } from "../../price";

export type ShimuraiSelection = {
  /** Every selected algo. Always at least one. */
  algos: Algo<AlgoConfiguration>[];
  /**
   * More than one algo selected — the only case that changes the form.
   *
   * A single shimurai algo is edited exactly like any other: every field live,
   * quantity and price included, submitted through the ordinary single-algo
   * update. `bulk` is what strips the form back to the one field a whole
   * selection can share, and disables the rest.
   */
  bulk: boolean;
};

/**
 * The selection when *every* algo in it is a `_shimurai` variant, else
 * `undefined`.
 *
 * All-or-nothing on purpose: `margin_override` exists only on shimurai algos,
 * so a mixed selection has no field the whole selection shares. That case falls
 * through to the ordinary multi-select behaviour — a blank form — rather than
 * silently overriding the subset that would accept it.
 */
export const useShimuraiSelection = (): ShimuraiSelection | undefined => {
  const { selectedAlgos } = useListedAlgo();

  return useMemo(() => {
    if (selectedAlgos.length === 0) {
      return undefined;
    }
    const allShimurai = selectedAlgos.every((algo) =>
      isShimuraiAlgoSpec(algo.algo_spec.name)
    );
    return allShimurai
      ? { algos: selectedAlgos, bulk: selectedAlgos.length > 1 }
      : undefined;
  }, [selectedAlgos]);
};

/**
 * A field `pick`ed off the selection, but only when every algo agrees on it —
 * `undefined` when they diverge, so the form shows blank rather than one
 * arbitrary algo's value.
 *
 * It matters most for the margin: prefilling one algo's would make "leave it
 * alone" indistinguishable from "set them all to this". For the disabled
 * fields it is only honesty about what the selection has in common.
 *
 * `eq` is for values `===` cannot compare — arrays, and anything boxed.
 */
const common = <T,>(
  algos: Algo<AlgoConfiguration>[],
  pick: (algo: Algo<AlgoConfiguration>) => T,
  eq: (a: T, b: T) => boolean = (a, b) => a === b
): T | undefined => {
  const first = pick(algos[0]);
  return algos.every((algo) => eq(pick(algo), first)) ? first : undefined;
};

export const useSelectedAlgo = (
  fieldRef?: React.RefObject<HTMLInputElement | null>,
  /** Focused instead of {@link fieldRef} on a bulk margin override, where the
   * quantity field `fieldRef` points at is not rendered. */
  marginFieldRef?: React.RefObject<HTMLInputElement | null>
) => {
  const { selectedAlgos } = useListedAlgo();
  const shimurai = useShimuraiSelection();
  const { reset } = usePrefillContext();
  const { setValues, values, resetForm } = useFormikContext<ConfigTabState>();

  /**
   * Which bulk selection the form was last populated from. The effect re-runs
   * on `values.*` changes, and without this it would overwrite the margin the
   * trader is halfway through typing.
   */
  const appliedBulkKey = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (shimurai?.bulk) {
      const bulkAlgos = shimurai.algos;
      const key = bulkAlgos
        .map((algo) => algo.algo_id)
        .sort()
        .join(",");
      if (appliedBulkKey.current === key) {
        return;
      }
      appliedBulkKey.current = key;

      // Everything but the margin is filled in for context only — those fields
      // are disabled, and `toMarginOverrideAlgo` builds the outbound requests
      // from the algos themselves, so nothing here reaches the wire. Where the
      // selection disagrees on a field it is left blank. `algo_id` stays unset
      // too; the submit reads the ids off the selection, not the form.
      const pdef = (algo: Algo<AlgoConfiguration>) =>
        algo.product_definitions[0];

      const grid = common(bulkAlgos, (a) => pdef(a).delivery_area_id);
      const deliveryStart = common(bulkAlgos, (a) => pdef(a).delivery_start);
      const deliveryEnd = common(bulkAlgos, (a) => pdef(a).delivery_end);
      const productTypes = common(
        bulkAlgos,
        (a) => pdef(a).product_types,
        (a, b) => a.join() === b.join()
      );
      const price = common(bulkAlgos, (a) =>
        "price" in a.configuration ? a.configuration.price : null
      );
      const marginOverride = common(
        bulkAlgos,
        (a) => a.configuration.margin_override ?? null
      );
      const quantity = common(bulkAlgos, (a) => a.configuration.quantity);
      const start = common(bulkAlgos, (a) => a.start);
      const end = common(bulkAlgos, (a) => a.end);

      setValues({
        ...initialState,
        product_definition: {
          grid: grid ?? "",
          // An existing algo's area is fixed, so there is nothing to choose.
          grid_options: [],
          delivery_start:
            deliveryStart === undefined ? undefined : dayjs(deliveryStart),
          delivery_end:
            deliveryEnd === undefined ? undefined : dayjs(deliveryEnd),
          display_grid: grid === undefined ? "" : find(grid).displayName,
          product_name:
            common(bulkAlgos, (a) => a.configuration.product_name) ?? "",
          prefilled_product_types: productTypes ?? [],
        },
        direction: common(bulkAlgos, (a) =>
          "direction" in a.configuration ? a.configuration.direction : undefined
        ),
        // A shimurai algo is neither behaviour, so nothing highlights — showing
        // the trader's last choice here would misdescribe the selection.
        // Cancelling back out restores the default; see `customResetForm`.
        behavior: undefined,
        // configuration.price is yen/MWh, the unit we send it in; the form is
        // yen/kWh.
        price: price != null ? Price.fromYenPerMwh(price).yenPerKwh : undefined,
        quantity,
        display_quantity: quantity,
        traded_quantity: common(bulkAlgos, (a) => a.state.trade_quantity),

        tag: common(bulkAlgos, (a) => a.configuration.tag) ?? "",
        // Display only, like the rest — the field is disabled.
        balancing_group:
          common(bulkAlgos, (a) => a.configuration.balancing_group) ??
          undefined,

        start: start === undefined ? undefined : dayjs(start),
        end: end === undefined ? undefined : dayjs(end),

        // configuration.margin_override is yen/MWh, like `price`; the form is
        // yen/kWh.
        margin_override:
          marginOverride != null
            ? Price.fromYenPerMwh(marginOverride).yenPerKwh
            : null,
      });

      focusField(marginFieldRef);
      reset();
      return;
    }
    appliedBulkKey.current = undefined;

    if (selectedAlgos.length !== 1) {
      if (values.algo_id) {
        customResetForm(values.behavior, resetForm);
      }
      return;
    }
    const selectedAlgo = selectedAlgos[0];

    if (selectedAlgo.algo_id === values.algo_id) {
      return;
    }

    // Only algos this tab knows how to edit populate the form. A shimurai algo
    // has no `Behavior` of its own but is still editable here — every field
    // live, plus the margin — so it is admitted explicitly.
    if (
      behaviorFromAlgoSpec(selectedAlgo.algo_spec.name) === undefined &&
      !isShimuraiAlgoSpec(selectedAlgo.algo_spec.name)
    ) {
      return;
    }

    const configuration = selectedAlgo.configuration;

    const pdef = selectedAlgo.product_definitions[0];
    const grid = pdef.delivery_area_id;
    const displayGrid = find(grid).displayName;

    let start: undefined | Dayjs =
      selectedAlgo.start === undefined ? undefined : dayjs(selectedAlgo.start);
    const end: undefined | Dayjs =
      selectedAlgo.end === undefined ? undefined : dayjs(selectedAlgo.end);
    if (start?.isBefore(dayjs())) {
      start = undefined;
    }
    const newState: ConfigTabState = {
      ...initialState,
      algo_id: selectedAlgo.algo_id,
      product_definition: {
        grid: grid,
        // An existing algo's area is fixed, so there is nothing to choose.
        grid_options: [],
        delivery_start: dayjs(pdef.delivery_start),
        delivery_end: dayjs(pdef.delivery_end),
        display_grid: displayGrid,
        product_name: configuration.product_name,
        prefilled_product_types: pdef.product_types,
      },
      direction:
        "direction" in configuration ? configuration.direction : undefined,
      // `undefined` for a shimurai variant this UI cannot create, which is
      // neither behaviour. Its buttons are already disabled — `BehaviorSwitch`
      // disables the lot when the single selected algo's spec maps to no
      // `Behavior`.
      behavior: behaviorFromAlgoSpec(selectedAlgo.algo_spec.name),
      // A terminator's behaviour, if it is one this desk offers. Anything else
      // leaves it unset, so the trader has to pick one before updating.
      terminator_behavior:
        "behaviors" in configuration &&
        isTerminatorBehavior(configuration.behaviors)
          ? configuration.behaviors
          : undefined,
      // Only shimurai algos carry one; `undefined` leaves the field unrendered,
      // so the nullish value is passed through rather than collapsed to null.
      // It is yen/MWh on the algo, like `price`; the form is yen/kWh.
      margin_override:
        configuration.margin_override != null
          ? Price.fromYenPerMwh(configuration.margin_override).yenPerKwh
          : configuration.margin_override,
      reference_price:
        configuration.reference_price != null
          ? Price.fromYenPerMwh(configuration.reference_price).yenPerKwh
          : configuration.reference_price,
      // LinearCloser only, yen/MWh like the rest.
      min_margin:
        configuration.min_margin != null
          ? Price.fromYenPerMwh(configuration.min_margin).yenPerKwh
          : configuration.min_margin,
      max_margin:
        configuration.max_margin != null
          ? Price.fromYenPerMwh(configuration.max_margin).yenPerKwh
          : configuration.max_margin,
      upper_omt_update_frequency: configuration.upper_omt_update_frequency,
      // configuration.price is yen/MWh, the unit we send it in; the form is
      // yen/kWh.
      price:
        "price" in configuration && configuration.price != null
          ? Price.fromYenPerMwh(configuration.price).yenPerKwh
          : undefined,
      quantity: configuration.quantity,

      start,
      end,

      tag: configuration.tag,
      // Display only: the field is disabled when editing, and the update sends
      // the algo's echoed group, never this — see `withEchoedBalancingGroup`.
      // `null` (an algo older than the field) becomes undefined, shown as "—".
      balancing_group: configuration.balancing_group ?? undefined,

      traded_quantity: selectedAlgo.state.trade_quantity,
      display_quantity:
        configuration.quantity !== null
          ? configuration.quantity - (selectedAlgo.state.trade_quantity ?? 0)
          : null,
    };

    setValues(newState);

    focusField(fieldRef);
    reset();
  }, [
    selectedAlgos,
    shimurai,
    reset,
    resetForm,
    fieldRef,
    marginFieldRef,
    setValues,
    values.algo_id,
    values.behavior,
  ]);
};

export const usePrefillFromOrderbook = (
  fieldRef?: React.RefObject<HTMLInputElement | null>
) => {
  const { prefill } = usePrefillContext();
  const { values, setValues } = useFormikContext<ConfigTabState>();
  useEffect(() => {
    if (prefill !== undefined) {
      focusField(fieldRef);
      const displayGrid = find(prefill.grid).displayName;
      const delivery_start = dayjs(prefill.deliveryStart);
      const one_hour_before_delivery = delivery_start.subtract(1, "hour");
      let end: undefined | Dayjs = undefined;
      // Default trade end to 1 hour before delivery when that is still in the
      // future. TODO: confirm with the APAC desk against JEPX gate closure.
      if (one_hour_before_delivery.isAfter(dayjs())) {
        end = one_hour_before_delivery;
      }
      let behavior = undefined;
      if (
        values.behavior &&
        Object.values(Behavior).includes(values.behavior)
      ) {
        behavior = values.behavior;
      }

      // Kept for the same reason as `behavior`: clicking a price should not
      // undo a choice the trader already made. Falls back to the default
      // when coming off an edit that left it unset.
      const terminator_behavior =
        values.terminator_behavior ?? DEFAULT_TERMINATOR_BEHAVIOR;

      const newState: ConfigTabState = {
        ...initialState,
        product_definition: {
          grid: prefill.grid,
          grid_options: prefill.gridOptions,
          delivery_start,
          delivery_end: dayjs(prefill.deliveryEnd),
          display_grid: displayGrid,
          product_name: prefill.product,
          prefilled_product_types: prefill.product_type,
        },
        direction: direction(prefill.direction),
        price: prefill.price?.yenPerKwh,
        behavior,
        terminator_behavior,
        // The algos that price off a reference start it at the spot of the
        // selected grid, rather than the clicked quote. `GridField` re-picks
        // it when the grid is switched. Blank when that area's day-ahead has
        // not cleared.
        reference_price: takesSpotReference({ behavior, terminator_behavior })
          ? prefill.spotByArea[prefill.grid]?.yenPerKwh
          : undefined,

        tag: "",

        prefill_id: uuidv4(),

        end,
      };

      setValues(newState);
    }
    // Ignoring behavior, as it caused prefill to trigger when behavior switches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill, setValues, fieldRef]);
};
